"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X, ArrowRight } from "lucide-react";
import { useTheme } from "next-themes";
import { Button } from "../ui/button";
import { getCookie } from "@/utils/cookieHandler";
import { isExpired } from "react-jwt";

const Header = () => {
    const router = useRouter();
    const { theme, setTheme } = useTheme();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mounted, setMounted] = useState(false);

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", handleScroll);
        setMounted(true);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const isLoggedIn = () => {
        const token = getCookie("token");
        if (!token || (token && isExpired(token))) return false;
        return true;
    };

    const handleLogin = () => {
        if (isLoggedIn()) {
            router.push("/analysis");
        } else {
            router.push("/login");
        }
    };

    const navLinks = [
        { name: "Features", href: "#features" },
        { name: "How it Works", href: "#how-it-works" },
        { name: "Benefits", href: "#benefits" },
    ];

    if (!mounted) return null;

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 px-6 py-4 ${scrolled
                ? "bg-white/70 dark:bg-gray-950/70 backdrop-blur-xl border-b border-gray-200/50 dark:border-white/10 shadow-lg py-3"
                : "bg-transparent py-5"
                }`}
        >
            <div className="max-w-7xl mx-auto flex items-center justify-between">
                {/* Logo */}
                <Link href="/" className="relative z-10 flex items-center gap-3 group">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300">
                        <Image
                            src="/images/fav_icon/Icom_Fav_icon.png"
                            alt="iComm Icon"
                            width={24}
                            height={24}
                        />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-xl font-black text-gray-950 dark:text-white leading-none tracking-tight">
                            iComm&trade;
                        </span>
                        <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-[0.25em] mt-1">
                            Platforms
                        </span>
                    </div>
                </Link>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center gap-1 bg-gray-100/50 dark:bg-white/5 p-1 rounded-2xl border border-gray-200/50 dark:border-white/10 backdrop-blur-md">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            href={link.href}
                            className="px-5 py-2 rounded-xl text-sm font-bold text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-white dark:hover:bg-white/10 transition-all"
                        >
                            {link.name}
                        </Link>
                    ))}
                </nav>

                {/* Actions */}
                <div className="flex items-center gap-3">
                    {/* Theme Toggle */}
                    <button
                        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                        className="p-2.5 rounded-xl bg-gray-100/50 dark:bg-white/5 border border-gray-200/50 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all"
                        aria-label="Toggle theme"
                    >
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>

                    {/* CTA Button */}
                    <Button
                        onClick={handleLogin}
                        className="hidden sm:flex group relative px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm transition-all overflow-hidden"
                    >
                        <span className="relative z-10 flex items-center gap-2">
                            {isLoggedIn() ? "Dashboard" : "Login"}
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                    </Button>

                    {/* Mobile Toggle */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="p-2.5 md:hidden rounded-xl bg-gray-100/50 dark:bg-white/5 border border-gray-200/50 dark:border-white/10 text-gray-600 dark:text-gray-400"
                    >
                        {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="absolute top-20 left-6 right-6 p-6 rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 shadow-2xl md:hidden flex flex-col gap-6"
                    >
                        <div className="flex flex-col gap-4">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    href={link.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="text-lg font-bold text-gray-900 dark:text-white"
                                >
                                    {link.name}
                                </Link>
                            ))}
                        </div>
                        <Button
                            onClick={() => {
                                handleLogin();
                                setMobileMenuOpen(false);
                            }}
                            className="w-full py-6 rounded-2xl bg-indigo-600 text-white font-black"
                        >
                            {isLoggedIn() ? "Enter Dashboard" : "Login to iComm"}
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
